/**
 * THE SHELL — FIVE PLACES, ONE CONVERSATION, AND NOTHING ELSE IN THE WAY.
 *
 * The information architecture is the Owner's decision of 2026-10-17: the Main
 * Chat is the product, and every other surface is one of four record areas
 * beside it. The sidebar carries exactly those five names. Every view that
 * existed before is a TAB inside its area — see `destinations.ts` — so the
 * filing cabinet is still complete and it is no longer tipped onto the floor.
 *
 * DESKTOP GETS A SIDEBAR, THE PHONE GETS FIVE TARGETS. The same table drives
 * both, so a destination cannot exist in one and be missing from the other.
 * A phone's bottom bar is the five areas and no "More": five fit a thumb, and
 * a sixth item that opens a second navigation is a menu about a menu.
 *
 * THE HEADER CARRIES NO COUNTS AND NO BADGES. A badge in a header is an
 * ambient urgency signal: it is visible from a screen the Owner is not
 * reading, it cannot be qualified, and it turns a count of unread records into
 * pressure. Counts live where their denominator lives.
 */

import { useEffect, useState } from 'preact/hooks';
import { signal } from '@preact/signals';
import {
	DESTINATIONS,
	DEFAULT_DESTINATION,
	areaTabFor,
	destinationById,
} from './destinations';
import { navigate, route, startRouter, type Route } from './router';
import { pathFor, pathForTab } from './paths';
import {
	ACCESS_LOGOUT_PATH,
	observeAuthenticationFailures,
	probeSession,
	session,
	signOutLocally,
} from './session';
import { watchHiddenTab } from './motion';
import { checkForShellUpdate, serviceWorkerState } from './service-worker-client';
import { Landing } from './views/landing';
import { StatusView } from './views/status';
import { ChatView } from './views/chat';
import { HomeView } from './views/home';
import { NotificationsView } from './views/notifications';
import { HistoryView } from './views/history';
import { ResourcesView } from './views/resources';
import { ResearchView } from './views/research';
import { ResearchControlView } from './views/research-control';
import { WorkspacesView, WorkspaceView } from './views/workspaces';
import { CapabilityLibraryView } from './views/capability-library';
import { MissionsView } from './views/missions';
import { ResearchTopicView } from './views/research-topic';
import { EvidenceView, KnowledgeView, ObservatoryView } from './views/science';
import { MediaView } from './views/media';
import { ExperimentsView } from './views/experiments';
import { ImprovementsView } from './views/improvements';
import { SearchView } from './views/search';
import { SettingsView } from './views/settings';

/** The sidebar's compact footer link to the device-and-session surface. It is
 *  not a sixth destination: it is where the settings TAB can also be found
 *  from the one place a desktop reader looks for the foot of a nav. */
function linkProps(id: string, extraClass = '') {
	return {
		href: `/${id}`,
		class: `nav-link${route.value.destination === id ? ' is-current' : ''}${extraClass}`,
		'aria-current': route.value.destination === id ? ('page' as const) : undefined,
		onClick: (event: MouseEvent) => {
			// A REAL LINK THAT ALSO ROUTES CLIENT-SIDE. The href is what makes it
			// a link — middle-click, long-press, "open in new tab" and a
			// screen-reader's link list all keep working — and the handler only
			// spares the page a reload. The one exception is a modified click,
			// which the browser must handle itself.
			if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
				return;
			}
			event.preventDefault();
			navigate({ destination: id, section: null, recordId: null });
		},
	};
}

function Sidebar() {
	const groups: string[] = [];
	for (const destination of DESTINATIONS) {
		const group = destination.group ?? 'Workspace';
		if (!groups.includes(group)) groups.push(group);
	}
	return (
		<nav class="sidebar" aria-label="Areas">
			<a {...linkProps(DEFAULT_DESTINATION, ' brand')}>
				<span class="brand-name">Scout</span>
				<span class="brand-note">research workspace</span>
			</a>
			{groups.map((group) => (
				<div class="nav-group" key={group}>
					<span class="nav-group-label">{group}</span>
					<ul class="nav-list">
						{DESTINATIONS.filter((destination) => (destination.group ?? 'Workspace') === group).map(
							(destination) => (
								<li key={destination.id}>
									<a {...linkProps(destination.id)} title={destination.blurb}>
										<span class="nav-label">{destination.label}</span>
									</a>
								</li>
							),
						)}
					</ul>
				</div>
			))}
			<a class="sidebar-foot" href="/workspace/settings" onClick={(event) => {
				if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
				event.preventDefault();
				navigate({ destination: 'workspace', section: 'settings', recordId: null });
			}}>
				Install & session
			</a>
		</nav>
	);
}

/**
 * THE AREA'S OWN TABS — the second level, and the only second level.
 *
 * A tab is a real link (middle-click works), and it names the view in one or
 * two words. There is no nested sidebar: the areas with the most views have
 * three tabs, and those fit on a phone as a wrapping row.
 */
function AreaTabs() {
	const current = route.value;
	const area = destinationById(current.destination);
	if (area === undefined || area.tabs.length === 0) return null;
	const active = areaTabFor(area.id, current.section);
	return (
		<nav class="area-tabs" aria-label={`${area.label} views`}>
			{area.tabs.map((tab) => {
				const isCurrent = active?.id === tab.id;
				const path = pathForTab(area.id, tab.section);
				return (
					<a
						key={tab.id}
						href={path}
						class={`area-tab${isCurrent ? ' is-current' : ''}`}
						aria-current={isCurrent ? ('page' as const) : undefined}
						title={tab.blurb}
						onClick={(event: MouseEvent) => {
							if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
							event.preventDefault();
							navigate({ destination: area.id, section: tab.section, recordId: null });
						}}
					>
						{tab.label}
					</a>
				);
			})}
		</nav>
	);
}

function TopBar() {
	return (
		<header class="topbar">
			<span class="topbar-brand">
				<svg class="topbar-mark" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
					<circle cx="12" cy="12" r="8.5" fill="none" stroke="currentColor" stroke-width="2" />
					<circle cx="12" cy="12" r="3" fill="currentColor" />
				</svg>
				<span class="topbar-name">Scout</span>
			</span>
		</header>
	);
}

function BottomBar() {
	const [open, setOpen] = useState(false);
	useEffect(() => {
		if (!open) return;
		const onKey = (event: KeyboardEvent) => {
			if (event.key === 'Escape') setOpen(false);
		};
		document.addEventListener('keydown', onKey);
		return () => document.removeEventListener('keydown', onKey);
	}, [open]);
	const primary = DESTINATIONS.filter((destination) => destination.inBottomBar === true);
	return (
		<>
			<nav class="bottombar" aria-label="Areas">
				{primary.map((destination) => (
					<a key={destination.id} {...linkProps(destination.id)}>
						{destination.barLabel ?? destination.label}
					</a>
				))}
				<button
					type="button"
					class={`bottombar-more${open ? ' is-current' : ''}`}
					aria-expanded={open}
					aria-haspopup="dialog"
					onClick={() => setOpen((current) => !current)}
				>
					More
				</button>
			</nav>
			{open && (
				<div class="sheet">
					<button
						type="button"
						class="sheet-scrim"
						aria-label="Close navigation"
						onClick={() => setOpen(false)}
					/>
					<div class="sheet-body" role="dialog" aria-modal="true" aria-label="Every area">
						<div class="sheet-head">
							<h2>Every area</h2>
							<button type="button" class="button ghost small" onClick={() => setOpen(false)}>
								Close
							</button>
						</div>
						<ul class="nav-list">
							{DESTINATIONS.map((destination) => (
								<li key={destination.id}>
									<a
										href={`/${destination.id}`}
										class={`nav-link${route.value.destination === destination.id ? ' is-current' : ''}`}
										aria-current={
											route.value.destination === destination.id ? ('page' as const) : undefined
										}
										onClick={(event: MouseEvent) => {
											if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
											event.preventDefault();
											setOpen(false);
											navigate({ destination: destination.id, section: null, recordId: null });
										}}
									>
										<span class="nav-label">{destination.label}</span>
										<span class="nav-blurb">{destination.blurb}</span>
									</a>
								</li>
							))}
						</ul>
					</div>
				</div>
			)}
		</>
	);
}

/**
 * A NEWER SHELL IS INSTALLED AND WAITING.
 *
 * The service worker deliberately does not call `skipWaiting()` on its own — a
 * worker that swaps the running page's world out mid-session would let a view
 * change its mind about a payload shape between two reads of the same
 * conversation. So the update is offered rather than taken, and taking it is
 * two steps the Owner can see: tell the worker to become current, then reload.
 */
function UpdateNotice() {
	const state = serviceWorkerState.value;
	if (!state.updateWaiting) return null;
	return (
		<div class="update-notice" role="status">
			<p>A newer version of this workspace is ready. Nothing changes until you reload.</p>
			<button
				type="button"
				class="button ghost"
				onClick={() => {
					void (async () => {
						await checkForShellUpdate();
						const registration = navigator.serviceWorker?.getRegistration === undefined
							? null
							: await navigator.serviceWorker.getRegistration('/');
						registration?.waiting?.postMessage({ type: 'scout:apply-update' });
						window.location.reload();
					})();
				}}
			>
				Reload
			</button>
		</div>
	);
}

/**
 * THE VIEW SWITCH — one branch per TAB, and the fallback is the area's own
 * first tab rather than an empty screen or a "not built" apology. The browser
 * acceptance walks `DESTINATIONS` and every tab, so a tab whose branch was
 * forgotten is caught by rendering it rather than by remembering.
 */
function CurrentView() {
	const current = route.value;
	const destination = destinationById(current.destination);
	if (destination === undefined) return null;
	switch (destination.id) {
		case 'chat':
			return <ChatView />;
		case 'research':
			// THE LIST IS `/research/windows`; ONE WINDOW IS
			// `/research/window/<runId>`. Both must mount this view: the singular
			// deep link is what the chat, search and spaces emit, and the view's
			// own effect only opens a run when it is mounted.
			if (current.section === 'window' || current.section === 'windows') {
				return <ResearchView mode="windows" />;
			}
			if (current.section === 'observatory') return <ObservatoryView />;
			if (current.section === 'experiments') return <ExperimentsView />;
			if (current.section === 'missions') return <MissionsView />;
			// ONE TOPIC, ONE COMPLETE PAGE. The directory stays at /research.
			if (current.section === 'topic') return <ResearchTopicView topicId={current.recordId} />;
			return <ResearchControlView />;
		case 'spaces':
			// THE WORKSPACE IS THE SECOND SEGMENT. `/spaces` is the directory;
			// `/spaces/<id>` is one programme's own environment.
			return current.section === null ? (
				<WorkspacesView />
			) : (
				<WorkspaceView spaceId={current.section} />
			);
		case 'foundry':
			if (current.section === 'capability') {
				return <CapabilityLibraryView capabilityId={current.recordId} />;
			}
			return <CapabilityLibraryView />;
		case 'discoveries':
			if (current.section === 'knowledge') return <KnowledgeView />;
			if (current.section === 'media') return <MediaView />;
			return <EvidenceView />;
		case 'history':
			if (current.section === 'search') return <SearchView />;
			return <HistoryView />;
		case 'workspace':
			if (current.section === null || current.section === 'overview') return <HomeView />;
			if (current.section === 'notifications') return <NotificationsView />;
			if (current.section === 'resources') return <ResourcesView />;
			if (current.section === 'status') return <StatusView />;
			if (current.section === 'settings') return <SettingsView />;
			if (
				current.section === 'improvements' ||
				current.section === 'charter' ||
				current.section === 'decisions' ||
				current.section === 'decision'
			) {
				return <ImprovementsView />;
			}
			return <HomeView />;
		default:
			return null;
	}
}

export function App() {
	useEffect(() => {
		const stop = startRouter();
		// THE OPENING PATH IS CANONICALISED ONCE, so `/` and every pre-area
		// deep link settle on the address this build actually serves rather than
		// leaving a URL the router privately disagrees with.
		if (window.location.pathname !== pathFor(route.value)) {
			navigate(route.value, { replace: true });
		}
		// THE SESSION IS ASKED ONCE, AND THE ASK IS THE GATE. Everything below
		// renders the landing until Scout has answered an owner-gated read.
		const stopObserving = observeAuthenticationFailures();
		void probeSession();
		// DECORATIVE MOTION STOPS WHEN NOBODY IS LOOKING.
		const stopWatching = watchHiddenTab();
		return () => {
			stop();
			stopObserving();
			stopWatching();
		};
	}, []);

	// A SESSION THAT HAS NOT ANSWERED YET IS NOT A LANDING AND NOT A WORKSPACE.
	if (session.value.kind === 'probing') {
		return (
			<main class="boot" id="content" aria-busy="true">
				<p class="boot-line" role="status">
					Asking Scout whether this browser has a session…
				</p>
			</main>
		);
	}

	if (session.value.kind !== 'signedIn') return <Landing />;

	const chatActive = route.value.destination === 'chat';
	return (
		<div class={`shell${chatActive ? ' shell-chat' : ''}`}>
			<Sidebar />
			<div class="shell-main">
				<TopBar />
				<main class={`content${chatActive ? ' content-chat' : ''}`} id="content">
					<AreaTabs />
					<UpdateNotice />
					<CurrentView />
				</main>
			</div>
			<BottomBar />
		</div>
	);
}

/**
 * THE SESSION'S OWN FOOTER, KEPT FOR THE SETTINGS TAB.
 *
 * It is exported rather than inlined there because the landing's copy of the
 * sign-out sentence is different — the landing was never signed in. This is the
 * signed-in control only, moved out of the sidebar so the five primary names
 * are the whole of the primary navigation.
 */
export function SessionNote() {
	const [busy, setBusy] = useState(false);
	const [outcome, setOutcome] = useState<string | null>(null);

	async function signOut(): Promise<void> {
		setBusy(true);
		const result = await signOutLocally();
		setOutcome(
			result.cachesCleared === null
				? 'This workspace signed out. There is no service worker here, so there was no shell cache to clear.'
				: result.cachesCleared
					? 'This workspace signed out and cleared the offline shell it had cached.'
					: 'This workspace signed out, and the offline shell cache did NOT confirm it was cleared.',
		);
		setBusy(false);
		window.location.assign(ACCESS_LOGOUT_PATH);
	}

	return (
		<div class="session-note">
			<p>
				Signed in as Scout’s Owner. This workspace holds no Scout credential — the session is the
				deployment’s identity provider’s.
			</p>
			{outcome !== null && <p role="status">{outcome}</p>}
			<button type="button" class="link-button" disabled={busy} onClick={() => void signOut()}>
				{busy ? 'Signing out…' : 'Sign out'}
			</button>
		</div>
	);
}
